import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import NeilMethodSection from "@/components/NeilMethodSection";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  BarChart3,
  CalendarCheck,
  Filter,
  Headphones,
  MessageSquare,
  PhoneCall,
  RefreshCw,
  Settings2,
  Target,
  Workflow,
} from "lucide-react";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const AGENTS = [
  { icon: PhoneCall, h: "AI Receptionist", p: "Answers every inbound call around the clock, routes by intent, captures details, and hands off to a human when it should." },
  { icon: Headphones, h: "AI Customer Service", p: "Handles repeat questions across voice, chat, and text so your team spends its time on the exceptions." },
  { icon: CalendarCheck, h: "AI Appointment Setter", p: "Books directly into your calendar with confirmations, reminders, and reschedule handling." },
  { icon: Target, h: "AI Sales Assistant", p: "Responds to new inquiries in seconds, answers product questions, and warms leads before your rep calls." },
  { icon: RefreshCw, h: "AI Follow-Up", p: "Multi-touch follow-up sequences across call, SMS, and email so no inquiry goes cold." },
  { icon: Settings2, h: "Administrative AI", p: "Data entry, note capture, document drafting, and internal request handling on autopilot." },
  { icon: Filter, h: "Lead Qualification", p: "Scores and routes inbound demand against your criteria so sales works the right list." },
  { icon: Workflow, h: "Workflow Automation", p: "Connects the steps between systems — intake, approval, dispatch, and closeout without manual handoffs." },
  { icon: MessageSquare, h: "CRM Automation", p: "Pipeline hygiene, stage triggers, task creation, and automated updates inside your existing CRM." },
  { icon: BarChart3, h: "Reporting Dashboards", p: "Call volume, response time, booked appointments, and conversion visible in one place." },
];

const PATH = [
  { h: "Map the work", p: "We document the calls, messages, and manual steps your team repeats every day." },
  { h: "Design the agents", p: "Scripts, guardrails, escalation rules, and integrations defined and approved by you." },
  { h: "Deploy and monitor", p: "Launch on a contained channel first, review real transcripts, and tune before expanding." },
  { h: "Scale across the operation", p: "Add channels, departments, and locations once the first workflow is proven." },
];

const AIWorkforce = () => (
  <>
    <SEO
      title="AI Workforce — AI Employees & Automation | Neil AI Global Solutions"
      description="AI receptionists, customer service agents, appointment setters, lead qualification, workflow and CRM automation. Digital automation that pairs with Neil AI robotics."
      keywords="AI employees, AI receptionist, AI customer service, AI appointment setter, CRM automation, workflow automation, AI workforce"
    />
    <Navigation />

    <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-4 space-y-16">
        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-14">
          <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
            AI Workforce
          </Badge>
          <h1 className="font-orbitron text-3xl md:text-5xl font-bold leading-tight max-w-4xl">
            Physical Automation + Digital Automation = Autonomous Workforce
          </h1>
          <p className="mt-5 max-w-3xl text-muted-foreground md:text-lg leading-relaxed">
            Robots handle the work on the floor. AI employees handle the work on the phone, in the
            inbox, and inside your systems. Neil AI builds both sides of the workforce so capacity
            grows without the hiring cycle.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Get an Automation Assessment <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <Link to="/robots">See the Robotics Side</Link>
            </Button>
          </div>
        </section>

        <section>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-8">Your AI Employees</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AGENTS.map((a) => {
              const Icon = a.icon;
              return (
                <Card key={a.h} className="glass-effect border-[hsl(var(--line))] p-6">
                  <Icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-orbitron font-bold text-base mb-2">{a.h}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{a.p}</p>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-12">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-8">How We Deploy It</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PATH.map((s, i) => (
              <div key={s.h} className="rounded-2xl border border-[hsl(var(--line))] bg-background/40 p-5">
                <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-bold mt-2 mb-1.5">{s.h}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-primary/30 bg-primary/[0.05] p-8 md:p-14 text-center">
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-4">
            Build your autonomous workforce.
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-7">
            Start with one workflow. Prove it. Then let the AI workforce and the robot fleet grow
            together under one program.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Get an Automation Assessment
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <Link to="/raas">Explore RaaS</Link>
            </Button>
          </div>
        </section>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-16">
        <div className="px-4"><NeilMethodSection compact contextLabel="N.E.I.L.™ WORKFLOW ASSESSMENT" contextCopy="Before we deploy AI employees, we map the workflow: what the work is, where it happens, what systems and people touch it today, and what it currently costs to run." /></div>
        <Footer />
      </div>
    </main>
      <section className="mx-auto w-full max-w-7xl px-[clamp(1rem,5vw,1.5rem)] pb-16">
        <a href="/robots/humanoid/healthcare" className="block rounded-2xl border border-[hsl(var(--line))] glass-effect p-6 hover:border-primary/50 transition-colors">
          <p className="text-xs uppercase tracking-wide text-accent">Humanoid Division</p>
          <p className="mt-2 font-orbitron text-lg font-bold">Humanoid Robotics for Healthcare &amp; Senior Care</p>
          <p className="mt-2 text-sm text-muted-foreground">Connected care technology, service humanoids, AI automation and Robotics-as-a-Service for hospitals and senior-living communities.</p>
        </a>
      </section>
  </>
);

export default AIWorkforce;
